/**
 * TSS Export API
 * # Overview This API describes the export interface to retrieve TSS log files from the Fiskal Cloud storage. All export files are encrypted with a customer specific key before storing in the Fiskal Cloud. The encryption used follows a [hybrid cryptosystem](https://en.wikipedia.org/wiki/Hybrid_cryptosystem) principle.  The steps for encryption are: * You generate a [RSA](https://en.wikipedia.org/wiki/RSA_(cryptosystem)) key pair in the Fiskal Cloud and get the private key exported for secure storage at your side. * The public key is kept in the Fiskal Cloud and used during the encryption process. * For encrypting the individual TSS log exports, a random symmetric [AES-256](https://en.wikipedia.org/wiki/Advanced_Encryption_Standard) key is generated that is used to encrypt the TSS log file. This AES key is then encrypted with the public key configured for the organization of the Fiskal Cloud Connector and stored inside the log file metadata. Encryption is done using the [Cipher block chain mode of AES](https://en.wikipedia.org/wiki/Block_cipher_mode_of_operation#Cipher-block_chaining_.28CBC.29). * Metadata and encrypted TSS log file are stored in the Fiskal Cloud.  Decryption follows the same principle: * Download the encrypted log file and get the encryption information from the metadata. * Use your private key to decrypt the AES key. * Use the AES key and the [Initialization Vector (IV)](https://en.wikipedia.org/wiki/Block_cipher_mode_of_operation#Initialization_vector_(IV)) from the metadata to decrypt the file.  # Usage There are multiple ways to access tss log files. Choose the one most appropriate for your use case.  ## TSS Export REST API By using the REST API described in this document you can request the log information per business premises and request direct download links for the log files.  > Please note that the files may be encrypted and need to be decrypted after download to be readable. > If the private key is stored in Fiskal Cloud you have the option to use the download with decryption on the fly.  The API provides functions to get all business premises with logs and retrieve log metadata and download information for single business-premises. Workflow steps: 1. Lookup business premises to get logs for download 2. Get Log Metadata & Download information 3. Download logs (decrypted or raw/encrypted) 4. Get Encryption info from download information (if downloaded encrypted)   a. Decrypt the file encryption key using your private key   b. Decrypt file with the file encryption key 5. Unzip log tar 6. Validate Checksum 7. Archive log tar on save storage 8. Acknowledge download  ### Getting an authentication token  To be able to work with the API you need to get an authentication token to use with all subsequent API calls described below. Get the token via the [Oauth endpoint](https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token) of the Fiskal Cloud. Use client_id=df-api and grant_type=password for requesting a token. > The access token grants same access rights as the user has in the Fiskal Cloud Portal.  Example request to get token via curl: ``` curl -X POST \\ https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token \\ -H \'Accept: application/json\' \\ -H \'Content-Type: application/x-www-form-urlencoded\' \\ -d \'username=<USER_EMAILADDRESS>&password=<USER_PASSWORD>&client_id=df-api&grant_type=password\' ``` Save the access_token entry of the response and pass it as bearer token in all subsequent API calls. Pass it as header parameter. ```\'Authorization: Bearer <ACCESS_TOKEN_FROM_AUTHENTICATION>\'```  For details on access tokens, please check general Oauth documentation like [Oauth.com](https://www.oauth.com/oauth2-servers/access-tokens/access-token-response/)  ## Fiskal Cloud Export Helper The Fiskal Cloud Export Helper provides an example how to access the tss logs via Java code on the command line.  ## Microsoft Azure Blob Storage The file storage is backed by Microsoft Azure Blob Storage and allows usage of specific Azure Blob APIs directly to access the files. This will allow the use of tooling and libraries provided by Microsoft to integrate the Tss log export into different environments. > Please note, that the use of Microsoft APIs is optional and use is on your own behalf. > DF Deutsche Fiskal is neither responsible nor liable for problems with using these and other third party tools.  For details on Blob Storage see the [Azure Blob Storage](https://docs.microsoft.com/en-us/azure/storage/blobs/) documentation. For information how to download and decrypt encrypted blobs using Microsoft API please see the [Client-Side Encryption](https://docs.microsoft.com/en-us/azure/storage/common/storage-client-side-encryption-java) documentation  ### Managed Tenant You can use a different tenant for operations if you have a permission to managed them. You can select managed tenant by sending `X-Managed-Tenant` header with `tenantId` of the managed tenant. All operations (creating, updating and getting) are done in the context of the managed tenant with this header.  ``` curl -X GET \\ -i \'https://fiskal.cloud/api/tsslog/business-premises\' \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'X-Managed-Tenant: {{managedTenantId}}\' ``` 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: info@deutsche-fiskal.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec, HttpContext 
        }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

// @ts-ignore
import { ArchiveMigrationDTO } from '../model/archive-migration-dto';
// @ts-ignore
import { BusinessPremisesLogInfoResponseDTO } from '../model/business-premises-log-info-response-dto';
// @ts-ignore
import { ExportStatusDTO } from '../model/export-status-dto';
// @ts-ignore
import { FilesNamesBodyDTO } from '../model/files-names-body-dto';
// @ts-ignore
import { MetaDataDTO } from '../model/meta-data-dto';
// @ts-ignore
import { MetaDataListResponseDTO } from '../model/meta-data-list-response-dto';
// @ts-ignore
import { MetaDataSummaryDTO } from '../model/meta-data-summary-dto';

// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    ExportLogsApiClientInterface,
    AcknowledgeLogRequestParams,
    AcknowledgeLogsRequestParams,
    AcknowledgeLogsBulkRequestParams,
    GetBusinessPremisesRequestParams,
    GetLogMetadataRequestParams,
    GetLogMetadataListRequestParams,
    GetMetaDataPagedRequestParams,
    GetMetaDataSummaryByTssIdRequestParams,
    MigrateToArchiveRequestParams
} from './export-logs-api-client.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class ExportLogsApiClient implements ExportLogsApiClientInterface {

    protected basePath = 'https://fiskal.cloud/api/tsslog';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string|string[], @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (Array.isArray(basePath) && basePath.length > 0) {
                basePath = basePath[0];
            }

            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    // @ts-ignore
    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Acknowledge a single log file
     * Change a single log file status to acknowledged, if not already with status removed
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public acknowledgeLog(requestParameters: AcknowledgeLogRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any>;
    public acknowledgeLog(requestParameters: AcknowledgeLogRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpResponse<any>>;
    public acknowledgeLog(requestParameters: AcknowledgeLogRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpEvent<any>>;
    public acknowledgeLog(requestParameters: AcknowledgeLogRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any> {
        const filename = requestParameters.filename;
        if (filename === null || filename === undefined) {
            throw new Error('Required parameter filename was null or undefined when calling acknowledgeLog.');
        }
        const businessPremisesId = requestParameters.businessPremisesId;
        if (businessPremisesId === null || businessPremisesId === undefined) {
            throw new Error('Required parameter businessPremisesId was null or undefined when calling acknowledgeLog.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/business-premises/${this.configuration.encodeParam({name: "businessPremisesId", value: businessPremisesId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}/logs/${this.configuration.encodeParam({name: "filename", value: filename, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}`;
        return this.httpClient.request<any>('put', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Acknowledge a list of log files.
     * Change log files status to acknowledged, if not already with status removed
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public acknowledgeLogs(requestParameters: AcknowledgeLogsRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any>;
    public acknowledgeLogs(requestParameters: AcknowledgeLogsRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpResponse<any>>;
    public acknowledgeLogs(requestParameters: AcknowledgeLogsRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpEvent<any>>;
    public acknowledgeLogs(requestParameters: AcknowledgeLogsRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any> {
        const businessPremisesId = requestParameters.businessPremisesId;
        if (businessPremisesId === null || businessPremisesId === undefined) {
            throw new Error('Required parameter businessPremisesId was null or undefined when calling acknowledgeLogs.');
        }
        const filesNamesBodyDTO = requestParameters.filesNamesBodyDTO;

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/business-premises/${this.configuration.encodeParam({name: "businessPremisesId", value: businessPremisesId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}/logs/bulk-acknowledgement`;
        return this.httpClient.request<any>('put', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: filesNamesBodyDTO,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Acknowledge a list of log files.
     * Change log files status to acknowledged, if not already with status removed
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public acknowledgeLogsBulk(requestParameters: AcknowledgeLogsBulkRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any>;
    public acknowledgeLogsBulk(requestParameters: AcknowledgeLogsBulkRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpResponse<any>>;
    public acknowledgeLogsBulk(requestParameters: AcknowledgeLogsBulkRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpEvent<any>>;
    public acknowledgeLogsBulk(requestParameters: AcknowledgeLogsBulkRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any> {
        const filesNamesBodyDTO = requestParameters.filesNamesBodyDTO;

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/logs/bulk-acknowledgement`;
        return this.httpClient.request<any>('put', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: filesNamesBodyDTO,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * List business premises with log files
     * Returns list of business premises which have not acknowledged log files.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getBusinessPremises(requestParameters: GetBusinessPremisesRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<BusinessPremisesLogInfoResponseDTO>;
    public getBusinessPremises(requestParameters: GetBusinessPremisesRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<BusinessPremisesLogInfoResponseDTO>>;
    public getBusinessPremises(requestParameters: GetBusinessPremisesRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<BusinessPremisesLogInfoResponseDTO>>;
    public getBusinessPremises(requestParameters: GetBusinessPremisesRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const pageSize = requestParameters.pageSize;
        const pageNumber = requestParameters.pageNumber;
        const sort = requestParameters.sort;

        let localVarQueryParameters = new HttpParams({encoder: this.encoder});
        if (pageSize !== undefined && pageSize !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageSize, 'pageSize');
        }
        if (pageNumber !== undefined && pageNumber !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageNumber, 'pageNumber');
        }
        if (sort !== undefined && sort !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>sort, 'sort');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/business-premises`;
        return this.httpClient.request<BusinessPremisesLogInfoResponseDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                params: localVarQueryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get metadata for single log
     * Retrieves a single log file. Will return metadata with link to download the TSS log file content.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getLogMetadata(requestParameters: GetLogMetadataRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<MetaDataDTO>;
    public getLogMetadata(requestParameters: GetLogMetadataRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<MetaDataDTO>>;
    public getLogMetadata(requestParameters: GetLogMetadataRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<MetaDataDTO>>;
    public getLogMetadata(requestParameters: GetLogMetadataRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const filename = requestParameters.filename;
        if (filename === null || filename === undefined) {
            throw new Error('Required parameter filename was null or undefined when calling getLogMetadata.');
        }
        const businessPremisesId = requestParameters.businessPremisesId;
        if (businessPremisesId === null || businessPremisesId === undefined) {
            throw new Error('Required parameter businessPremisesId was null or undefined when calling getLogMetadata.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/business-premises/${this.configuration.encodeParam({name: "businessPremisesId", value: businessPremisesId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}/logs/${this.configuration.encodeParam({name: "filename", value: filename, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}`;
        return this.httpClient.request<MetaDataDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Query log files per single business premises
     * Allows you to query log files for a single business premises and filter by status.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getLogMetadataList(requestParameters: GetLogMetadataListRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<MetaDataListResponseDTO>;
    public getLogMetadataList(requestParameters: GetLogMetadataListRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<MetaDataListResponseDTO>>;
    public getLogMetadataList(requestParameters: GetLogMetadataListRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<MetaDataListResponseDTO>>;
    public getLogMetadataList(requestParameters: GetLogMetadataListRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const businessPremisesId = requestParameters.businessPremisesId;
        if (businessPremisesId === null || businessPremisesId === undefined) {
            throw new Error('Required parameter businessPremisesId was null or undefined when calling getLogMetadataList.');
        }
        const status = requestParameters.status;
        const tssIds = requestParameters.tssIds;
        const pageSize = requestParameters.pageSize;
        const pageNumber = requestParameters.pageNumber;
        const sort = requestParameters.sort;

        let localVarQueryParameters = new HttpParams({encoder: this.encoder});
        if (status) {
            status.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'status');
            })
        }
        if (tssIds) {
            tssIds.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'tssIds');
            })
        }
        if (pageSize !== undefined && pageSize !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageSize, 'pageSize');
        }
        if (pageNumber !== undefined && pageNumber !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageNumber, 'pageNumber');
        }
        if (sort !== undefined && sort !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>sort, 'sort');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/business-premises/${this.configuration.encodeParam({name: "businessPremisesId", value: businessPremisesId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}/logs`;
        return this.httpClient.request<MetaDataListResponseDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                params: localVarQueryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get list of metadata
     * Retrieves a list of uploaded metadata
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMetaDataPaged(requestParameters: GetMetaDataPagedRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<MetaDataListResponseDTO>;
    public getMetaDataPaged(requestParameters: GetMetaDataPagedRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<MetaDataListResponseDTO>>;
    public getMetaDataPaged(requestParameters: GetMetaDataPagedRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<MetaDataListResponseDTO>>;
    public getMetaDataPaged(requestParameters: GetMetaDataPagedRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const bpIds = requestParameters.bpIds;
        const orgIds = requestParameters.orgIds;
        const tssIds = requestParameters.tssIds;
        const uploadTimeFrom = requestParameters.uploadTimeFrom;
        const uploadTimeTo = requestParameters.uploadTimeTo;
        const status = requestParameters.status;
        const pageNumber = requestParameters.pageNumber;
        const pageSize = requestParameters.pageSize;
        const sort = requestParameters.sort;

        let localVarQueryParameters = new HttpParams({encoder: this.encoder});
        if (bpIds) {
            bpIds.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'bpIds');
            })
        }
        if (orgIds) {
            orgIds.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'orgIds');
            })
        }
        if (tssIds) {
            tssIds.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'tssIds');
            })
        }
        if (uploadTimeFrom !== undefined && uploadTimeFrom !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>uploadTimeFrom, 'uploadTimeFrom');
        }
        if (uploadTimeTo !== undefined && uploadTimeTo !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>uploadTimeTo, 'uploadTimeTo');
        }
        if (status) {
            status.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'status');
            })
        }
        if (pageNumber !== undefined && pageNumber !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageNumber, 'pageNumber');
        }
        if (pageSize !== undefined && pageSize !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageSize, 'pageSize');
        }
        if (sort !== undefined && sort !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>sort, 'sort');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/logs`;
        return this.httpClient.request<MetaDataListResponseDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                params: localVarQueryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get a specific MetaDataSummary object by tss id
     * Get meta data summary entity by tss id
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMetaDataSummaryByTssId(requestParameters: GetMetaDataSummaryByTssIdRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<MetaDataSummaryDTO>;
    public getMetaDataSummaryByTssId(requestParameters: GetMetaDataSummaryByTssIdRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<MetaDataSummaryDTO>>;
    public getMetaDataSummaryByTssId(requestParameters: GetMetaDataSummaryByTssIdRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<MetaDataSummaryDTO>>;
    public getMetaDataSummaryByTssId(requestParameters: GetMetaDataSummaryByTssIdRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const tssId = requestParameters.tssId;
        if (tssId === null || tssId === undefined) {
            throw new Error('Required parameter tssId was null or undefined when calling getMetaDataSummaryByTssId.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/tss/${this.configuration.encodeParam({name: "tssId", value: tssId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}/logs/metadata-summary`;
        return this.httpClient.request<MetaDataSummaryDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Migrate logs to archive
     * Migrates logs of current tenant to archive. It\&#39;s possible to refine the filter for logs.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public migrateToArchive(requestParameters: MigrateToArchiveRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any>;
    public migrateToArchive(requestParameters: MigrateToArchiveRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpResponse<any>>;
    public migrateToArchive(requestParameters: MigrateToArchiveRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpEvent<any>>;
    public migrateToArchive(requestParameters: MigrateToArchiveRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any> {
        const archiveMigrationDTO = requestParameters.archiveMigrationDTO;

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (export_auth) required
        localVarCredential = this.configuration.lookupCredential('export_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/tenants/tenant/archive-migrations`;
        return this.httpClient.request<any>('post', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: archiveMigrationDTO,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
